/* Déposer, consulter, retirer un document d'usine.
 *
 * Le fichier va dans le bac privé `pieces`, déjà utilisé par les factures
 * usine ; la ligne en base ne porte que de quoi le retrouver et le classer.
 * Les deux vont ensemble : un fichier déposé dont la ligne échoue est aussitôt
 * retiré, sinon le bac se remplit d'orphelins que plus rien ne nomme.
 */

import { supabase } from "@/lib/supabase";
import { natureProbable, refusDe, titreProbable } from "@/domain/documents-usine";
import type { DocumentUsine, NatureDocument } from "@/types/metier";

export class DepotRefuse extends Error {}

/** Un nom de fichier sûr pour le stockage, l'original restant affiché tel quel. */
function nomSur(nom: string): string {
  return nom
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-zA-Z0-9._-]+/g, "-")
    .slice(-80);
}

export async function deposerDocument(
  usineId: string,
  fichier: File,
  champs?: { nature?: NatureDocument; titre?: string; produitId?: string | null; langue?: string },
): Promise<DocumentUsine> {
  const refus = refusDe(fichier);
  if (refus) throw new DepotRefuse(refus);

  const chemin = `usines/${usineId}/${Date.now()}-${nomSur(fichier.name)}`;
  const { error: erreurBac } = await supabase.storage.from("pieces").upload(chemin, fichier);
  if (erreurBac) throw new Error(erreurBac.message);

  const { data, error } = await supabase
    .from("documents_usine")
    .insert({
      usine_id: usineId,
      produit_id: champs?.produitId ?? null,
      nature: champs?.nature ?? natureProbable(fichier.name),
      titre: champs?.titre?.trim() || titreProbable(fichier.name),
      langue: champs?.langue ?? null,
      nom: fichier.name,
      chemin,
      taille: fichier.size,
      type_mime: fichier.type || null,
    })
    .select()
    .single();

  if (error) {
    /* La ligne n'est pas passée : on ne laisse pas le fichier derrière. */
    await supabase.storage.from("pieces").remove([chemin]);
    throw new Error(error.message);
  }
  return data as DocumentUsine;
}

/** Lien de consultation, valable une heure : le bac n'est pas public. */
export async function lienDocument(chemin: string): Promise<string | null> {
  const { data, error } = await supabase.storage.from("pieces").createSignedUrl(chemin, 3600);
  if (error) return null;
  return data?.signedUrl ?? null;
}

/** Retire la ligne, puis le fichier. Dans cet ordre : une ligne sans fichier
    se voit et se corrige ; un fichier sans ligne ne se voit plus. */
export async function retirerDocument(doc: Pick<DocumentUsine, "id" | "chemin">): Promise<void> {
  const { error } = await supabase.from("documents_usine").delete().eq("id", doc.id);
  if (error) throw new Error(error.message);
  await supabase.storage.from("pieces").remove([doc.chemin]);
}

export async function renommerDocument(
  id: string,
  champs: { titre?: string; nature?: NatureDocument; produit_id?: string | null;
            langue?: string | null; diffusable?: boolean },
): Promise<void> {
  const { error } = await supabase.from("documents_usine").update(champs).eq("id", id);
  if (error) throw new Error(error.message);
}
